import type { WorldBuilderResult } from '@creanizer/types'
import { Alert, AlertDescription } from '@creanizer/ui'
import { AlertTriangle, Shield, Swords } from 'lucide-react'

interface WorldThreatsProps {
  threats?: WorldBuilderResult['details']['threats']
  dangerLevel: number
}

export function WorldThreats({ threats, dangerLevel }: WorldThreatsProps) {
  if (!threats) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        脅威データがありません
      </div>
    )
  }
  
  return (
    <div className="space-y-6">
      {/* 危険度インジケーター */}
      <div className="flex items-center gap-3 p-3 bg-gradient-to-r from-red-50 to-orange-50 rounded-lg">
        <AlertTriangle className="h-5 w-5 text-red-600" />
        <div>
          <div className="font-semibold">世界危険度: {dangerLevel}/10</div>
          <div className="text-sm text-muted-foreground">
            {dangerLevel >= 8 ? '極めて危険な世界' :
             dangerLevel >= 6 ? '危険が常に存在する世界' :
             dangerLevel >= 4 ? '一定の危険がある世界' :
             '比較的安全な世界'}
          </div>
        </div>
      </div>
      
      {/* 敵性存在 */}
      {threats.existsHostileForce && Array.isArray(threats.hostileForces) && threats.hostileForces.length > 0 && (
        <div>
          <h3 className="font-semibold mb-3 flex items-center gap-2">
            <Swords className="h-4 w-4" />
            敵性存在
          </h3>
          <div className="space-y-4">
            {threats.hostileForces.map((force, index) => (
              <div key={index} className="border border-red-200 rounded-lg p-4 space-y-3">
                <div>
                  <div className="flex items-start justify-between">
                    <h4 className="font-semibold text-lg text-red-700">
                      {force.name}
                    </h4>
                    <span className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded">
                      {force.type}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-2">
                    {force.description}
                  </p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  <div>
                    <div className="font-medium text-red-600 mb-1">脅威レベル</div>
                    <div className="text-muted-foreground">{force.threat}</div>
                  </div>
                  
                  {Array.isArray(force.capabilities) && force.capabilities.length > 0 && (
                    <div>
                      <div className="font-medium text-red-600 mb-1">能力</div>
                      <div className="flex flex-wrap gap-1">
                        {force.capabilities.map((cap, idx) => (
                          <span key={idx} className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded">
                            {cap}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {Array.isArray(force.weaknesses) && force.weaknesses.length > 0 && (
                    <div>
                      <div className="font-medium text-green-600 mb-1">弱点</div>
                      <div className="flex flex-wrap gap-1">
                        {force.weaknesses.map((weak, idx) => (
                          <span key={idx} className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded">
                            {weak}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {Array.isArray(force.samples) && force.samples.length > 0 && (
                    <div className="md:col-span-2">
                      <div className="font-medium text-gray-600 mb-1">個体例</div>
                      <ul className="list-disc list-inside text-muted-foreground space-y-1">
                        {force.samples.map((sample, idx) => (
                          <li key={idx}>{sample}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* 紛争状況 */}
      {threats.conflictSituation && (
        <div>
          <h3 className="font-semibold mb-3 flex items-center gap-2">
            <Shield className="h-4 w-4" />
            世界情勢と紛争
          </h3>
          
          {Array.isArray(threats.conflictSituation.currentWars) && threats.conflictSituation.currentWars.length > 0 && (
            <div className="mb-4">
              <h4 className="text-sm font-semibold mb-2">現在進行中の戦争</h4>
              <div className="space-y-3">
                {threats.conflictSituation.currentWars.map((war, index) => (
                  <div key={index} className="p-3 bg-orange-50 rounded-lg">
                    <div className="font-medium text-orange-700">
                      {war.name}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2 text-sm">
                      <div>
                        <span className="text-muted-foreground">参加勢力: </span>
                        <span>{war.participants.join(' vs ')}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">原因: </span>
                        <span>{war.cause}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">現状: </span>
                        <span>{war.status}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">影響: </span>
                        <span>{war.impact}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {Array.isArray(threats.conflictSituation.tensions) && threats.conflictSituation.tensions.length > 0 && (
            <div className="mb-4">
              <h4 className="text-sm font-semibold mb-2">緊張状態</h4>
              <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
                {threats.conflictSituation.tensions.map((tension, idx) => (
                  <li key={idx}>{tension}</li>
                ))}
              </ul>
            </div>
          )}
          
          {Array.isArray(threats.conflictSituation.dangerZones) && threats.conflictSituation.dangerZones.length > 0 && (
            <div className="mb-4">
              <h4 className="text-sm font-semibold mb-2">危険地帯</h4>
              <div className="flex flex-wrap gap-2">
                {threats.conflictSituation.dangerZones.map((zone, idx) => (
                  <span key={idx} className="px-3 py-1 text-sm bg-red-100 text-red-700 rounded-full">
                    {zone}
                  </span>
                ))}
              </div>
            </div>
          )}
          
          {Array.isArray(threats.conflictSituation.securityMeasures) && threats.conflictSituation.securityMeasures.length > 0 && (
            <div className="mb-4">
              <h4 className="text-sm font-semibold mb-2">安全対策</h4>
              <div className="flex flex-wrap gap-2">
                {threats.conflictSituation.securityMeasures.map((measure, idx) => (
                  <span key={idx} className="px-3 py-1 text-sm bg-green-100 text-green-700 rounded-full">
                    {measure}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* 世界的な危険 */}
      {Array.isArray(threats.worldDangers) && threats.worldDangers.length > 0 && (
        <div>
          <h3 className="font-semibold mb-3">潜在的な世界の危機</h3>
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              <ul className="list-disc list-inside space-y-1 mt-2">
                {threats.worldDangers.map((danger, idx) => (
                  <li key={idx}>{danger}</li>
                ))}
              </ul>
            </AlertDescription>
          </Alert>
        </div>
      )}
    </div>
  )
}